import { Injectable, Injector } from '@angular/core';
import { Observable,tap, of, map } from 'rxjs';
import { AppComponentBase } from '@shared/common/app-component-base';
import { ApplicationStatus, CreateNpboApplicationDto, CreateOrUpdateNpboAddressInput, CreateOrUpdateNpboNumberInput, CreateOrUpdateNpboRegisterOutputDto, NpboAddressDefaultDataDto, NpboAddressDto, NpboAddressServiceProxy, NpboDto, NpboNumberDto, NpboNumberServiceProxy, NpboRegisterDto, NpboRegisterServiceProxy, NpboRemoteAddressDto, NpboServiceProxy, NpboStatus, PersonApplicationSummaryDto } from '@shared/service-proxies/service-proxies';
import { ApplicationService } from '../application/application.service';

@Injectable({
    providedIn: 'root',
})
export class NpboService extends AppComponentBase {
    npbo: NpboDto;

    constructor(injector: Injector, 
      private readonly npboServiceProxy: NpboServiceProxy,
      private readonly npboRegisterServiceProxy: NpboRegisterServiceProxy,
      private readonly applicationService: ApplicationService,
      private readonly npboAddressServiceProxy: NpboAddressServiceProxy,
      private readonly npboNumberServiceProxy: NpboNumberServiceProxy,
    ) {
        super(injector);
    }

    getMyNpbos(): Observable<PersonApplicationSummaryDto[]> {
        return this.npboServiceProxy.getMyNpbos();
    }

    createNpbo(professionId: string, legitimationId: number): Observable<string> {
        this.closeNpbo();
        return this.npboServiceProxy.createNpbo(new CreateNpboApplicationDto({
            professionId: professionId,
            legitimationId: legitimationId
        }));
    }

    getNpbo(caseId: string): Observable<NpboDto> {
        return this.npboServiceProxy.getNpbo(caseId).pipe(
            tap((output) => {
                this.npbo = output;
            })
        );
    }

    closeNpbo(): void {
        this.npbo = null;
    }

    get caseId(): string {
        return this.npbo.application.caseId;
    }

    get status(): ApplicationStatus {
        return this.npbo.application.status;
    }

    get profession(): string {
        return this.npbo?.professionId;
    }

    get medRegId(): number {
        return this.npbo.register.medRegId;
    }

    get hasMedRegId(): boolean {
        return this.npbo.register.medRegId != null;
    }

    get psyRegId(): number {
        return this.npbo.register.psyRegId;
    }

    get hasPsyRegId(): boolean {
        return this.npbo.register.psyRegId != null;
    }

    get naregId(): number {
        return this.npbo.register.naregId;
    }

    get hasNaregId(): boolean {
        return this.npbo.register.naregId != null;
    }     

    isInNpboStatus(expectedStatus: NpboStatus): boolean {
      return this.npbo.currentStep === expectedStatus;
    }

    getLegitimatedGlnForRegister(): Observable<string> {
      return this.npboRegisterServiceProxy.getLegitimatedGlnForRegister(this.caseId);
    }

    getRegister(): Observable<NpboRegisterDto> {
      return of (this.npbo.register);
    }

    createOrUpdateRegister(): Observable<CreateOrUpdateNpboRegisterOutputDto> {
      return this.npboRegisterServiceProxy.createOrUpdateRegister(this.caseId);
    }

    acceptRegisterCheck(): Observable<NpboStatus> {
      return this.npboRegisterServiceProxy.acceptRegisterCheck(this.caseId);
    }

    setRegisterStepToReview() {
      this.applicationService.setStepToReview(NpboStatus.Register);
    }

    getAddress(): Observable<NpboAddressDto> {
      return of(this.npbo.address);
    }

    getRemoteAddressses(): Observable<NpboRemoteAddressDto[]> {
      return this.npboAddressServiceProxy.getRemoteAddresses(this.caseId);
    }

    getDefaultAddressData(): Observable<NpboAddressDefaultDataDto> {
      return this.npboAddressServiceProxy.getDefaultData(this.caseId);
    }

    createOrUpdateAddress(input: CreateOrUpdateNpboAddressInput): Observable<void> {
        this.applicationService.setStepToReview(NpboStatus.Address);
        return this.npboAddressServiceProxy.createOrUpdateAddress(input).pipe(tap((status: NpboStatus) => {
            this.npbo.address = input.address;
            this.npbo.currentStep = status;
        }))
        .pipe(map((status: NpboStatus) => null));
    }

    getNumber(): Observable<NpboNumberDto> {
      return of(this.npbo.number);
    }

    createOrUpdateNumber(input: CreateOrUpdateNpboNumberInput): Observable<void> {
      this.applicationService.setStepToReview(NpboStatus.Number);
      return this.npboNumberServiceProxy.createOrUpdateNumber(input).pipe(tap((status: NpboStatus) => {
            this.npbo.number = input.number;
            this.npbo.currentStep = status;
      }))
      .pipe(map((status: NpboStatus) => null));
    }

    releaseNpbo(comment: string): Observable<void> {
      return this.npboServiceProxy.releaseNpbo(this.caseId, comment);
    }

    getNextStatus(status: NpboStatus): Observable<NpboStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.npboServiceProxy.getNextStatus(this.caseId, status).pipe(map(status => {
            // Authority goes to 'finish review' and not to 'release'
            if (isAuthority && status === NpboStatus.Release) {
                return NpboStatus.FinishReview;
            }
            return status;
        }));
    }

    getPersonId(): Observable<number> {
        return this.npboServiceProxy.getPersonId(this.caseId);
    }

    getStepUrl(nextStep: NpboStatus): string {
      return this.applicationService.getNextStep(NpboService.getUrlForStep(nextStep));
    }
    
    static getUrlForStep(step: NpboStatus): string {
        switch (step) {
            case NpboStatus.Register:
                return 'register';
            case NpboStatus.Address:
                return 'address';
            case NpboStatus.Number:
                return 'number';
            case NpboStatus.Release:
                return 'release';
            case NpboStatus.FinishReview:
                return 'finish-review';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }    
}
